import {
  useGoogleLogin,
  googleLogout,
  TokenResponse,
} from "@react-oauth/google";
// import { getGoogleSignoutConfig } from "components/auth/GoogleSignin";
import {
  Pages,
  AuthProvider,
  PlanOnelinkLimit,
  PlanProfessional,
  Plans,
  PlanNonProfit,
  PlanFree,
} from "appConfig";
import {
  apiRegisterGoogle,
  apiRegisterEmailPassword,
  apiAccountUpdate,
  apiGetPaymentCustomerPortalUrl,
  apiAccountDelete,
} from "net/requests";
import { apiSignInGoogle, apiSignInEmailPassword } from "net/requests";
import { storeAccessToken } from "./auth_utils";
import { getLanguageString as _ } from "misc/lang";

import {
  DefaultProps,
  AuthProps,
  DefaultAndAuthProps,
  DefaultAndOnelinkProps,
} from "model/props";
import { Account, Identity } from "model/account";
import { User } from "model/user";
import { LoaderUtil } from "./loader";
import { AccountInfo } from "model/account";

export type OnFailureFn = (authProvider: string, error: any) => void;

export const useSignOut = (props: AuthProps) => {
  const { authProvider, onSignedOut } = props;

  const signOutSuccess = (response: any = null) => {
    console.log("signed out");
    onSignedOut && onSignedOut();
  };

  // const onFailure = (response: any) => {
  //   onSignedOut && onSignedOut();
  // };

  // const googleConfig = getGoogleSignoutConfig(signOutSuccess, onFailure);

  const signOutGoogle = () => {
    googleLogout();
    signOutSuccess();
  };

  const signOutEmailPassword = () => {
    signOutSuccess();
  };

  switch (authProvider) {
    case AuthProvider.GOOGLE:
      return signOutGoogle;
    case AuthProvider.EMAIL_PASSWORD:
    default:
      return signOutEmailPassword;
  }
};

export const useRegister = (
  props: DefaultAndAuthProps,
  onFailure: OnFailureFn
) => {
  const { invitationToken, attribution } = props;

  // GOOGLE
  const googleSuccess = (tokenResponse: TokenResponse) => {
    const apiRequest = apiRegisterGoogle(tokenResponse, attribution, invitationToken);
    signInRegister(props, onFailure, apiRequest, AuthProvider.GOOGLE);
  };

  const googleFailure = (error: any) => onFailure(AuthProvider.GOOGLE, error);

  const googleRegister = useGoogleLogin({
    onSuccess: googleSuccess,
    onError: googleFailure,
    scope: "https://www.googleapis.com/auth/userinfo.profile",
  });

  // EMAIL + PASSWORD
  const registerEmailPassword = (email: string, password: string) => {
    const apiRequest = apiRegisterEmailPassword(
      email,
      password,
      attribution,
      invitationToken
    );
    signInRegister(props, onFailure, apiRequest, AuthProvider.EMAIL_PASSWORD);
  };

  return {
    registerEmailPassword: registerEmailPassword,
    registerGoogle: googleRegister,
  };
};

export const useSignIn = (
  props: DefaultAndAuthProps,
  onFailure: OnFailureFn
) => {
  // GOOGLE
  const googleSuccess = (tokenResponse: TokenResponse) => {
    const apiRequest = apiSignInGoogle(tokenResponse);
    signInRegister(props, onFailure, apiRequest, AuthProvider.GOOGLE);
  };
  const googleFailure = (error: any) => onFailure(AuthProvider.GOOGLE, error);

  const _signInGoogle = useGoogleLogin({
    onSuccess: googleSuccess,
    onError: googleFailure,
    scope: "https://www.googleapis.com/auth/userinfo.profile",
  });

  // EMAIL + PASSWORD
  const signInEmailPassword = (email: string, password: string) => {
    const apiRequest = apiSignInEmailPassword(email, password);
    signInRegister(props, onFailure, apiRequest, AuthProvider.EMAIL_PASSWORD);
  };

  return {
    signInEmailPassword: signInEmailPassword,
    signInGoogle: _signInGoogle,
  };
};

const signInRegister = (
  props: DefaultAndAuthProps,
  onFailure: OnFailureFn,
  apiRequest: any,
  authProvider: string
) => {
  const { setAuthProvider } = props;

  apiRequest
    .then((response: any) => {
      setAuthProvider(authProvider);
      onSignedIn(props, response.data);
    })
    .catch((error: any) => {
      onFailure(authProvider, error.response?.data?.error);
    });
};

export const onSignedIn = (
  props: DefaultAndAuthProps,
  identity: Identity,
  defaultOnSuccessNavigation: boolean = true
) => {
  const {
    setUser,
    setAccount,
    setLegacyInfo,
    navigate,
    routePendingAction,
    cookies,
    setServerTime,
  } = props;

  setUser(identity.user);
  setAccount(identity.account);
  setLegacyInfo(identity.legacyAccount);
  setServerTime && setServerTime(identity.timestamp);
  if (cookies?.experience) storeAccessToken(identity.user?.accessToken);

  if (!defaultOnSuccessNavigation) return;

  if (identity.account?.type === "pending") {
    navigate(Pages.COMPLETE_REGISTRATION);
    return;
  }

  if (routePendingAction(identity.account?.pendingAction as string)) return;

  navigate(Pages.MY_ONELINKS);
};

export const updateAccount = (
  props: DefaultProps,
  data: Account,
  callback?: (success: boolean) => void
) => {
  const { loader, user, setAccount, routePendingAction } = props;

  const loaderTurnedOn = loader.setLoading();
  apiAccountUpdate(user as User, data)
    .then((response) => {
      setAccount(response.data);
      if (response.data.pendingAction && routePendingAction) {
        routePendingAction(response.data.pendingAction);
      }
      callback && callback(true);
      loaderTurnedOn && loader.reset();
    })
    .catch((error) => {
      console.log(error.response?.data?.error);
      callback && callback(false);
      loader.setResult(
        false,
        _("general", "network_error_general_head"),
        _("general", "network_error_general")
      );
    });
};

export const suggestPlan = (onelinkCount: number, account: Account): string => {
  if (!account) return PlanProfessional;

  const nonProfitEligable =
    account.type === "nonprofit" || account.type === "individual";

  let nextPlan = PlanProfessional;

  const currentLimit = account.currentPlan === PlanFree ? 1 : PlanOnelinkLimit[account.currentPlan as string];

  for (const plan of Plans) {
    if (plan === PlanFree) continue;
    if (plan === PlanNonProfit && !nonProfitEligable) continue;
    if (account.currentPlan === plan) continue;

    const planOnelinkLimit = PlanOnelinkLimit[plan];

    if ((planOnelinkLimit > 0) && (Math.max(currentLimit, onelinkCount) >= planOnelinkLimit)) continue;

    nextPlan = plan;
    break;
  }

  return nextPlan;
};

export const actionUpgradePlan = (props: DefaultAndOnelinkProps) => {
  const { account, onelinkList, navigate } = props;

  if (!hasAddress(account as Account)) {
    navigate(Pages.ACCOUNT, undefined, undefined, "add_address");
    return;
  }

  const nextPlan = suggestPlan(onelinkList?.length || 0, account as Account);
  const data: Account = {
    uuid: account?.uuid,
    pendingPlan: nextPlan,
    pendingPlanPeriod: "monthly",
  };

  updateAccount(props, data);
};

export const actionOpenCustomerPortal = (user: User, loader: LoaderUtil) => {
  loader.setLoading();
  apiGetPaymentCustomerPortalUrl(user)
    .then((response) => {
      window.open(response.data.url, "_blank", "noopener");
      loader.reset();
    })
    .catch((error) => {
      loader.reset();
      if (error && error.response.status === 404) {
        alert("No payment account found");
      }
      const errorReason = error.response?.data?.error;
      console.log("Error: " + errorReason);
    });
};

export const hasAddress = (account: Account): boolean => {
  if (!account?.info) return false;

  const info: AccountInfo = account.info;
  const hasAddress =
    (!!info.addressLine1 && info.addressLine1.trim().length >= 1) &&
    (!!info.addressPostalCode && info.addressPostalCode.trim().length >= 1) &&
    (
      (!!info.addressRegion && info.addressRegion.trim().length >= 1) ||
      (!!info.addressCity && info.addressCity.trim().length >= 1)
    ) &&
    (!!info.country && info.country.trim().length >= 1);
  return hasAddress;
};


export const deleteAccount = (
  props: DefaultProps
) => {
  const { loader, user, account, onSignedOut } = props;

  loader.setLoading();
  apiAccountDelete(user as User, account?.uuid as string)
    .then((response) => {
      onSignedOut && onSignedOut();
      loader.setResult(true, _("general", "account_deleted_head"), _("general", "account_deleted_info"));
    })
    .catch((error) => {
      console.log(error.response?.data?.error);
      loader.setResult(
        false,
        _("general", "network_error_general_head"),
        _("general", "network_error_general")
      );
    });
};